import { Edge, Graph, Node, PointLike } from "@antv/x6"
import { Menu } from "antd"
import { getLineEndType } from "../../utils"
import { useMemo } from "react"
import { LineEndEnum, LineJumpType, NodeShapes, SheetContent } from "../../services/modal"
import { cellToNodeState } from "../../services/storage"
import { NozzleDirection } from "@/types/common"
import { SvgStreamDirection } from "../../nodes/SvgStream"
import { EventMap, useEditorEventBusInstance } from "../../events/useEventBusInstance"
import { useAddLineJump } from "../../hooks/useAddLineJump"
import { useCurrentSheet } from "../../hooks/useCurrentSheet"

interface EdgeContextMenuProps {
  edge: Edge,
  sheets: SheetContent[]
  graph: Graph
  onClose: () => void
  clickPoint: PointLike
}
export const EdgeContextMenu = (props: EdgeContextMenuProps) => {
  const {edge, sheets, graph, onClose, clickPoint} = props
  const source = edge.getSource();
  const target = edge.getTarget();
  const selectedSheet = useCurrentSheet()
  const eventBus = useEditorEventBusInstance()
  const addLineJump = useAddLineJump()

  const sheetOptions = sheets.filter(item => item.id !== selectedSheet?.id).map((item) => ({
    key: item.id, 
    label: item.name
  }))


  const connectionOptions = useMemo(() => {
    const sourceType = getLineEndType(source)
    const targetType = getLineEndType(target)
    if (sheetOptions.length === 0) {
      return []
    }
    const itemOptions: any = []
    if (sourceType === LineEndEnum.Point) {
      const simDirection = getEndDirection(target, graph)
      if (simDirection === NozzleDirection.Inlet) {
        itemOptions.push({ key: `source:${SvgStreamDirection.In}`,  label: 'Continue Inlet to', children: sheetOptions },)
      } else if (simDirection === NozzleDirection.Outlet) {
        itemOptions.push({ key: `source:${SvgStreamDirection.Out}`, label: 'Continue Outlet to', children: sheetOptions },)
      } else {
        itemOptions.push({ key: `source:${SvgStreamDirection.In}`, label: 'Continue Inlet to', children: sheetOptions },)
      }
    }
    if (targetType === LineEndEnum.Point) {
      const simDirection = getEndDirection(source, graph)
      if (simDirection === NozzleDirection.Inlet) {
        itemOptions.push({ key: `target:${SvgStreamDirection.In}`, label: 'Continue Inlet to', children: sheetOptions },)
      } else if (simDirection === NozzleDirection.Outlet) {
        itemOptions.push({ key: `target:${SvgStreamDirection.Out}`, label: 'Continue Outlet to', children: sheetOptions },)
      } else {
        itemOptions.push({ key: `target:${SvgStreamDirection.Out}`, label: 'Continue Outlet to', children: sheetOptions },)
      }
    }

    return itemOptions;
  }, [sheetOptions, source, target, graph])

  const jumpOptions = useMemo(() => {
    return [
      {key: 'lineJump', label: 'Line Jump', children: [
        {key: LineJumpType.Circle, label: 'Circle'},
        {key: LineJumpType.Blank, label: 'Blank'},
        {key: LineJumpType.Wave, label: 'Wave'}
      ]}
    ]
  }, [])

  const onSelect = async (item) => {
    const [secondKey, firstKey] = item.keyPath
    if (firstKey === 'lineJump') {
      addLineJump(graph, {
        edge: edge,
        point: clickPoint,
        type: secondKey
      })
      return;
    }
    const [endType, streamDir] = firstKey.split(':')
    eventBus.trigger(EventMap.addSvgStream, {
      edge: edge,
      sheetId: secondKey,
      endType,
      streamDir
    })
    onClose()
  }

  const options = [...jumpOptions, ...connectionOptions]
  if (options.length === 0) return;


  return <Menu className='orbit-context-menu' items={options} onSelect={onSelect} />
}

function getEndDirection(end: any, graph: Graph): NozzleDirection | undefined {
  const endType = getLineEndType(end)
  if (endType === LineEndEnum.LinkToPort) {
    const {cell, port: portId} = end;
    const node = graph.getCellById(cell) as Node
    if (!node || node.shape !== NodeShapes.SvgNode) return;
    const nodeState = cellToNodeState(node)
    const nozzle = nodeState.nozzles.find(item => item.id === portId)
    if (!nozzle) return;
    return nozzle.direction
  }
  return
}
